import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { projectService } from '../../../services/projectService';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

const ProjectSelector = ({ currentProjectId }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }

    const loadProjects = async () => {
      try {
        const data = await projectService?.getAll();
        setProjects(data);
      } catch (err) {
        console.error('Failed to load projects:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [user, authLoading]);

  const currentProject = projects?.find(p => p?.id === currentProjectId);

  const handleProjectChange = (projectId) => {
    navigate(`/project-comparison?project=${projectId}`);
    setIsOpen(false);
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-white border border-border rounded-lg">
        <Icon name="Loader" size={16} className="text-text-secondary animate-spin" />
        <span className="text-sm text-text-secondary">Loading stacks...</span>
      </div>
    );
  }

  if (!user || projects?.length === 0) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between gap-3 px-4 py-2.5 bg-white border border-border rounded-lg hover:border-primary hover:bg-blue-50 transition-all min-w-[300px] max-w-[500px]"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Icon name="FolderOpen" size={18} className="text-primary flex-shrink-0" />
          <div className="flex flex-col items-start min-w-0">
            <span className="text-xs text-text-secondary font-medium">Comparing Stack:</span>
            <span className="text-sm font-semibold text-foreground truncate max-w-full">
              {currentProject?.name || 'Select a Stack'}
            </span>
          </div>
        </div>
        <Icon 
          name={isOpen ? "ChevronUp" : "ChevronDown"} 
          size={18} 
          className="text-text-secondary flex-shrink-0" 
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown */}
          <div className="absolute top-full left-0 mt-2 w-full bg-white border border-border rounded-lg shadow-lg z-20 max-h-[400px] overflow-y-auto">
            <div className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-text-secondary uppercase tracking-wide">
                Select Stack to Compare
              </div>
              {projects?.map((project) => {
                const isSelected = project?.id === currentProjectId;
                const quotesCount = project?.quotesReceived || 0;
                
                return (
                  <button
                    key={project?.id}
                    onClick={() => handleProjectChange(project?.id)}
                    className={`w-full flex items-start gap-3 px-3 py-3 rounded-md transition-colors ${
                      isSelected 
                        ? 'bg-blue-50 border border-primary' :'hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    <div className="flex-1 min-w-0 text-left">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-sm font-semibold truncate ${
                          isSelected ? 'text-primary' : 'text-foreground'
                        }`}>
                          {project?.name}
                        </span>
                        {isSelected && (
                          <Icon name="Check" size={14} className="text-primary flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-text-secondary">
                        <span className="flex items-center gap-1">
                          <Icon name="Tag" size={12} />
                          {project?.category}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="FileText" size={12} />
                          {quotesCount} {quotesCount === 1 ? 'quote' : 'quotes'}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          project?.status === 'active' ? 'bg-green-100 text-green-700' :
                          project?.status === 'completed'? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {project?.status}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectSelector;